import { SignupVenueData, SignupVenueDataSchema } from '@blazebite/shared-models';
import { VENUE_DATA_KEYS } from './storageKey';

/**
 * The submission payload shape is owned by @blazebite/shared-models
 * (SignupVenueData — the venueData half of the signup-submitSignupProspect
 * contract). Re-exported under the app's historical name.
 */
export type VenueData = SignupVenueData;

// Default values for specific fields
const FIELD_DEFAULTS: Partial<Record<keyof VenueData, unknown>> = {
  billingFrequency: 'monthly',
  internetAvailability: true
};

// Fields that need boolean transformation
const BOOLEAN_FIELDS: (keyof VenueData)[] = ['internetAvailability'];

export class VenueDataMapper {
  /**
   * Maps localStorage values directly to VenueData.
   * LocalStorageKey values match VenueData field names exactly.
   */
  static mapFromLocalStorage(storage: Record<string, unknown>): VenueData {
    const venueData: Partial<VenueData> = {};

    VENUE_DATA_KEYS.forEach(key => {
      const fieldName = key as keyof VenueData;
      let value = storage[key];

      // Apply default if value is empty
      if (value === null || value === undefined || value === '') {
        value = FIELD_DEFAULTS[fieldName];
      }

      // Transform boolean fields
      if (BOOLEAN_FIELDS.includes(fieldName) && value !== null && value !== undefined) {
        if (typeof value === 'string') {
          value = value.toLowerCase() === 'true';
        } else {
          value = Boolean(value);
        }
      }

      if (value !== null && value !== undefined) {
        (venueData as Record<string, unknown>)[fieldName] = value;
      }
    });

    return venueData as VenueData;
  }

  static getRequiredFields(): (keyof VenueData)[] {
    return ['venueName', 'venueType', 'selectedPlan', 'userEmail'];
  }

  /**
   * Validates against the shared SignupVenueDataSchema — the same contract
   * the backend expects. Returns the offending field names (missing or
   * invalid) for the review page's error message.
   */
  static validateVenueData(venueData: Partial<VenueData>): { isValid: boolean; missingFields: string[] } {
    const result = SignupVenueDataSchema.safeParse(venueData);
    if (result.success) {
      return { isValid: true, missingFields: [] };
    }
    const missingFields = [...new Set(
      result.error.issues.map(issue => String(issue.path[0] ?? 'payload'))
    )];
    return { isValid: false, missingFields };
  }
}
